/** A session card (`.sess`), used on the start and join screens. */
import { Badge, Button } from '../../../components/ui';
import { t } from '../../../i18n/fr';
import { ProgressBar } from './ProgressBar';

import type { SessionWithExtras } from '../../../api/types';

export interface SessionCardAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface SessionCardProps {
  session: SessionWithExtras;
  /** Tapping the card itself, e.g. to go to the join list. */
  onSelect?: () => void;
  /** A primary action rendered at the bottom of the card, e.g. "Rejoindre". */
  action?: SessionCardAction;
  /** Show participants and a progress bar (the join screen's fuller card). */
  detailed?: boolean;
}

/**
 * Render a session card: name, kind badge, creator, headcount and, in
 * `distance_m` was requested with `?near=`, the rough distance.
 *
 * @param props - The session and optional selection/action behaviour.
 * @returns The card element.
 */
export function SessionCard({ session, onSelect, action, detailed = false }: SessionCardProps) {
  const kindLabel =
    session.kind === 'registration'
      ? t('session.card.kindRegistration')
      : t('session.card.kindStandard');
  const kindTone = session.kind === 'registration' ? 'yellow' : 'violet';
  const binomesLabel =
    session.participants.length === 1
      ? t('session.card.binome')
      : t('session.card.binomes', { count: session.participants.length });
  const total =
    session.progress.recorded + session.progress.pending + session.progress.candidates_left;

  const body = (
    <>
      <div className="titre">{session.name}</div>
      <div className="ligne">
        <Badge tone={kindTone}>{kindLabel}</Badge>
        <span className="muted">
          {t('session.card.by', { name: session.created_by.first_name })} · {binomesLabel}
          {session.distance_m !== undefined
            ? ` · ${t('session.card.distance', { distance: Math.round(session.distance_m) })}`
            : ''}
        </span>
      </div>
      {detailed ? (
        <>
          <div className="ligne">
            {session.participants.map((participant) => (
              <Badge key={participant.id} tone="neutral">
                {participant.user.first_name}
                {participant.current_door ? ` · ${participant.current_door}` : ''}
              </Badge>
            ))}
          </div>
          <p className="muted" style={{ margin: 'var(--space-2) 0' }}>
            {t('session.card.doorsProgress', { recorded: session.progress.recorded, total })}
          </p>
          <ProgressBar
            value={session.progress.recorded}
            total={total}
            label={t('session.card.doorsProgress', { recorded: session.progress.recorded, total })}
          />
        </>
      ) : null}
      {action ? (
        <Button
          variant="primary"
          fullWidth
          style={{ marginTop: 'var(--space-3)' }}
          disabled={action.disabled ?? false}
          onClick={(event) => {
            event.stopPropagation();
            action.onClick();
          }}
        >
          {action.label}
        </Button>
      ) : null}
    </>
  );

  // A card never combines a clickable container with a nested action
  // button (invalid HTML): `action` wins when both are somehow given.
  if (onSelect && !action) {
    return (
      <button type="button" className="sess" onClick={onSelect}>
        {body}
      </button>
    );
  }

  return <div className="sess">{body}</div>;
}
